/**
 * Tour Registry — single source of truth for every Guided Tour in the catalog.
 *
 * Each tour:
 *   id          — kebab-case identifier (used in URL: /tours/run/:id)
 *   title       — display title on the index card
 *   tagline     — short pitch under the title
 *   audience    — array of audience tags ("Funders", "New CMs", ...)
 *   duration    — approximate minutes (display only)
 *   icon        — lucide-react icon name (resolved at render time)
 *   accent      — Tailwind colour key used for the card accent
 *   status      — "live" | "coming-soon"
 *   scenes      — (live tours only) ordered scene definitions
 *
 * Scenes are defined in tour_*.js files. Importing them lazily keeps the
 * landing/index page light.
 */

// ══════════════════════════════════════════════════════════════════
//  Collections — narrative arcs stitched from multiple tours.
//
//  Some tours are single-purpose demos ("Disbursement Approval"), and
//  some are legs of a longer story. The ToursIndex renders any tour that
//  declares `collection: '<id>'` grouped and ordered by its `sequence`
//  under the collection's poster block, so a viewer can pick up the arc
//  ("One Morning at District: funder call → program live → neighbor
//  applies → PM approves") in the intended order.
// ══════════════════════════════════════════════════════════════════
export const COLLECTIONS = [
  {
    id: 'one-morning',
    title: 'One Morning at District',
    tagline: 'Follow a single case across four roles \u2014 funder call at 9:47, Case Manager on the case by 11:00. Watch each leg in order for the full ~12 minute arc.',
    accent: 'amber',
    icon: 'Sunrise',
  },
];

export const TOUR_CATALOG = [
  {
    id: 'neighbor-intake-eligibility',
    title: 'Neighbor Intake & Eligibility',
    tagline: 'A self-registered neighbor flows from approval to enrollment in 4 minutes.',
    audience: ['Funders', 'New CMs', 'Board'],
    duration: 4,
    icon: 'UserPlus',
    accent: 'sky',
    status: 'live',
    flagship: true,
    sceneCount: 8,
    loader: () => import('./tour_intake_eligibility.js'),
  },
  {
    id: 'disbursement-approval',
    title: 'Disbursement Approval',
    tagline: 'Emergency Assistance request → PM approval → audit + payment scheduled.',
    audience: ['Funders', 'PMs'],
    duration: 4,
    icon: 'DollarSign',
    accent: 'emerald',
    status: 'live',
    sceneCount: 8,
    loader: () => import('./tour_disbursement_approval.js'),
  },
  {
    id: 'one-day-in-district',
    title: 'One Day in District',
    tagline: 'Follow one neighbor across all 5 personas — the 7-minute board demo.',
    audience: ['Board', 'Funders'],
    duration: 7,
    icon: 'Sunrise',
    accent: 'amber',
    flagship: true,
    status: 'live',
    sceneCount: 11,
    loader: () => import('./tour_one_day_in_district.js'),
  },
  {
    id: 'configure-program',
    title: 'Configure a New Program',
    tagline: 'Admin builds a program, attaches tools & UIF fields — live instantly.',
    audience: ['Admins', 'Funders'],
    duration: 4,
    icon: 'Package',
    accent: 'violet',
    status: 'live',
    sceneCount: 9,
    collection: 'one-morning',
    sequence: 1,
    clockLabel: '9:47 AM',
    loader: () => import('./tour_configure_program.js'),
  },
  {
    id: 'build-reporting-period',
    title: 'Build a Reporting Period',
    tagline: 'Grants Manager pulls indicator rollups and exports a funder report.',
    audience: ['Funders', 'Grants team'],
    duration: 5,
    icon: 'FileBarChart',
    accent: 'rose',
    status: 'coming-soon',
  },
  // —— Backlog (Coming soon) ——
  { id: 'walk-in-crisis',         title: 'Walk-In Crisis Triage',            tagline: 'Same-day emergency rental assistance, lobby to disbursement.',     audience: ['New CMs'],            duration: 3, icon: 'AlertTriangle', accent: 'orange', status: 'coming-soon' },
  { id: 'cm-crisis',              title: 'Case Manager: Crisis Between Appointments', tagline: 'A distressing SMS lands at 12:47 PM. Four minutes to triage, decide, and document — without missing the 1:15.', audience: ['Funders', 'New CMs', 'Board'], duration: 5, icon: 'ShieldAlert', accent: 'rose', flagship: true, status: 'live', sceneCount: 9, loader: () => import('./tour_cm_crisis.js') },
  { id: 'cm-new-monday',          title: 'New Case Manager Starts Monday',    tagline: 'Marcus\u2019s first day \u2014 login at 8:52, six neighbors reviewed, one escalation, one signed EOD rollup. What used to take a week of ramp, done by 4:47 PM.', audience: ['New CMs', 'Board', 'Funders', 'HR'], duration: 7, icon: 'Sunrise', accent: 'teal', flagship: true, status: 'live', sceneCount: 10, loader: () => import('./tour_cm_new_monday.js') },
  { id: 'partner-referral',       title: 'Partner Referral & Capacity',       tagline: 'A CM ranks partners by confidence, sends the referral, watches the partner accept and close the loop — 90 seconds instead of 20 phone-tag minutes.', audience: ['Funders', 'Partners', 'New CMs'], duration: 5, icon: 'GitMerge', accent: 'indigo', flagship: true, status: 'live', sceneCount: 10, loader: () => import('./tour_partner_referral.js') },
  { id: 'grant-kpi-reporting',    title: 'Grant / KPI Reporting',              tagline: 'One case note becomes program data, a grant indicator tick, a funder report paragraph, and an executive rollup — across the real Grants, Reports, and Funder Goals pages.', audience: ['Funders', 'Grants team', 'Executives'], duration: 5, icon: 'Sparkles', accent: 'violet', flagship: true, status: 'live', sceneCount: 8, loader: () => import('./tour_grant_kpi.js') },
  { id: 'community-to-neighbor',  title: 'Community Member → Neighbor',        tagline: 'Event configured, RSVP taken, attendance checked, three weeks of touchpoints, one tap promotion — every step on the real Admin Events, Portal, Registration Desk, and PM roster screens.', audience: ['PMs', 'Community team', 'Funders'], duration: 6, icon: 'UserPlus', accent: 'sky', flagship: true, status: 'live', sceneCount: 11, loader: () => import('./tour_community_to_neighbor.js') },
  { id: 'referral-loop',          title: 'Referral Loop',                    tagline: 'CM ↔ Community Partner ↔ neighbor timeline.',                       audience: ['Partners'],            duration: 3, icon: 'Share2',         accent: 'teal',   status: 'coming-soon' },
  { id: 'escalation-resolve',     title: 'Escalation & Resolve',             tagline: 'Focus Mode: CM raises a blocker, PM resolves it.',                  audience: ['New CMs', 'PMs'],     duration: 3, icon: 'AlertCircle',    accent: 'rose',   status: 'coming-soon' },
  { id: 'neighbor-apply',         title: 'Neighbor: Apply for a Program',    tagline: 'Self-register, complete UIF, get enrolled — 2 minutes.',            audience: ['Board', 'Neighbors'], duration: 2, icon: 'Send',           accent: 'sky',    status: 'live', sceneCount: 6, collection: 'one-morning', sequence: 2, clockLabel: '10:13 AM', loader: () => import('./tour_neighbor_apply.js') },
  { id: 'pm-approve',             title: 'PM: Approve a Neighbor Request',   tagline: 'PM opens the queue, one-tap approve, watch auto-assignment fire.',  audience: ['Board', 'Funders', 'PMs'], duration: 3, icon: 'CheckCheck',     accent: 'violet', status: 'live', sceneCount: 6, collection: 'one-morning', sequence: 3, clockLabel: '10:15 AM', loader: () => import('./tour_pm_approve.js') },
  { id: 'cm-first-touch',         title: 'Case Manager: First-Touch Intake', tagline: 'Case Manager opens the profile and launches the guided intake.',   audience: ['Board', 'New CMs'],  duration: 2, icon: 'MessageSquareText', accent: 'teal',   status: 'live', sceneCount: 5, collection: 'one-morning', sequence: 4, clockLabel: '11:00 AM', loader: () => import('./tour_cm_first_touch.js') },
  { id: 'neighbor-appointment',   title: 'Neighbor: Book an Appointment',    tagline: 'Browse programs, pick a CM, confirm.',                              audience: ['Neighbors'],          duration: 1, icon: 'CalendarCheck',  accent: 'sky',    status: 'coming-soon' },
  { id: 'doc-upload-recompute',   title: 'Doc Upload → Eligibility Recompute', tagline: 'Watch a paystub unlock new programs in real time.',              audience: ['Board', 'Funders'],   duration: 2, icon: 'FileUp',         accent: 'emerald',status: 'coming-soon' },
  { id: 'story-approval',         title: 'Funder Story Approval',            tagline: 'Contributor writes a story → PM approves → ready for funder.',     audience: ['Funders'],            duration: 2, icon: 'BookOpen',       accent: 'amber',  status: 'coming-soon' },
  { id: 'compliance-drill',       title: 'Compliance Health Drill-In',       tagline: 'Executive view of red flags, holds, and chains.',                    audience: ['Executives'],         duration: 3, icon: 'ShieldAlert',    accent: 'rose',   status: 'coming-soon' },
  { id: 'sub-award-review',       title: 'Sub-Award Reviewer Flow',          tagline: 'Submissions, verdicts, take-overs, accept or revise.',              audience: ['Grants team'],        duration: 4, icon: 'ClipboardCheck', accent: 'violet', status: 'coming-soon' },
  { id: 'author-uif-question',    title: 'Author a UIF Question',            tagline: 'Add a question with eligibility logic and publish.',                 audience: ['Admins'],             duration: 3, icon: 'HelpCircle',     accent: 'violet', status: 'coming-soon' },
  { id: 'roles-matrix',           title: 'Roles & Permissions Matrix',       tagline: 'Visualize who can see and do what across the platform.',            audience: ['Auditors', 'Board'],  duration: 2, icon: 'Shield',         accent: 'slate',  status: 'coming-soon' },
  { id: 'event-registration',     title: 'Event Registration & Attendance',  tagline: 'Admin publishes → neighbor RSVPs → check-in at the door.',          audience: ['Community team'],     duration: 3, icon: 'CalendarDays',   accent: 'teal',   status: 'coming-soon' },
  { id: 'bulk-sms-outreach',      title: 'Bulk SMS Outreach',                tagline: 'Filtered cohort message in three clicks.',                           audience: ['PMs'],                duration: 2, icon: 'MessageSquare',  accent: 'sky',    status: 'coming-soon' },
  { id: 'yw-summer-payroll',      title: 'Youth Worker: Summer Payroll',     tagline: 'Attendance → PM approval → payroll export.',                        audience: ['YW supervisors'],     duration: 4, icon: 'Wallet',         accent: 'emerald',status: 'coming-soon' },
  { id: 'yw-behavior-incident',   title: 'Behavior Incident → Escalation',   tagline: 'YW logs an incident, escalation routes to PM.',                     audience: ['YW', 'PMs'],          duration: 3, icon: 'AlertOctagon',   accent: 'orange', status: 'coming-soon' },
];

export const findTour = (id) => TOUR_CATALOG.find(t => t.id === id);

/**
 * nextTourInCollection(currentId) — return the tour with the next-higher
 * `sequence` inside the same collection, or null if this is the last leg.
 * Powers the "Play from the top" auto-chain on the Tours index: when a
 * chained tour ends, TourContext.endTour looks this up and navigates
 * straight into the next leg instead of dumping the viewer back at
 * `/tours`, so a funder demo runs the whole arc hands-free.
 */
export const nextTourInCollection = (currentId) => {
  const cur = findTour(currentId);
  if (!cur?.collection) return null;
  const peers = TOUR_CATALOG
    .filter(t => t.collection === cur.collection && t.status === 'live')
    .sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
  const idx = peers.findIndex(t => t.id === currentId);
  return idx >= 0 && idx < peers.length - 1 ? peers[idx + 1] : null;
};
